import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { ArrowLeft, Shield, Eye, Handshake, FileCheck, Users, Lock } from 'lucide-react';

const Integrity = () => {
  const integrityPrinciples = [
    {
      icon: <Eye className="h-8 w-8" />,
      title: "Transparency",
      description: "We maintain open and honest communication in all our business dealings, from pricing to project timelines."
    },
    {
      icon: <Handshake className="h-8 w-8" />,
      title: "Ethical Practices",
      description: "We uphold the highest ethical standards in our work, ensuring fairness and respect in all interactions."
    },
    {
      icon: <FileCheck className="h-8 w-8" />,
      title: "Accountability",
      description: "We take responsibility for our actions and decisions, learning from mistakes and continuously improving."
    },
    {
      icon: <Lock className="h-8 w-8" />,
      title: "Data Protection",
      description: "We safeguard user data with the highest security standards and respect privacy as a fundamental right."
    }
  ];

  const integrityActions = [
    {
      title: "Open Communication",
      description: "Regular project updates, clear documentation, and honest discussions about challenges and opportunities.",
      metrics: "100% project transparency"
    },
    {
      title: "Fair Pricing",
      description: "No hidden fees, clear contracts, and pricing that reflects genuine value delivered to clients.",
      metrics: "Zero hidden costs"
    },
    {
      title: "Quality Assurance",
      description: "Rigorous testing, code reviews, and quality checkpoints ensure we deliver what we promise.",
      metrics: "99.9% reliability"
    },
    {
      title: "Privacy Protection",
      description: "GDPR compliance, secure data handling, and user control over personal information.",
      metrics: "Bank-level security"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/">
          <Button variant="outline" className="mb-8 text-vincotek-blue border-vincotek-blue hover:bg-vincotek-blue hover:text-white rounded-full transition-all duration-300 group">
            <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </Button>
        </Link>
      </div>

      {/* Hero Section */}
      <section className="pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-24 h-24 bg-gradient-to-br from-vincotek-yellow to-vincotek-blue rounded-full flex items-center justify-center mx-auto mb-8">
              <Shield className="h-12 w-12 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-vincotek-dark leading-tight mb-6">
              Integrity
            </h1>
            <p className="text-xl text-vincotek-blue font-semibold mb-6">
              Building Trust Through Honesty and Transparency
            </p>
            <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto mb-12">
              Integrity is the foundation of everything we do at Vincotek. We believe that trust is earned through 
              consistent, honest actions and transparent communication. Our commitment to integrity shapes how we 
              interact with clients, partners, and each other.
            </p>
            
            <div className="bg-gradient-to-br from-gray-50 to-blue-50/30 rounded-2xl p-8 border border-gray-100">
              <blockquote className="text-2xl font-medium text-vincotek-dark italic mb-4">
                "Integrity is doing the right thing, even when no one is watching."
              </blockquote>
              <p className="text-gray-600">
                — Core principle guiding all Vincotek operations
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Integrity Principles */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
              Our Integrity Framework
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These principles ensure that integrity is embedded in every aspect of our business operations.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {integrityPrinciples.map((principle, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white border-0">
                <CardContent className="p-8">
                  <div className="text-vincotek-blue mb-6 group-hover:scale-110 transition-transform duration-300">
                    {principle.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-vincotek-dark mb-4">
                    {principle.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {principle.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Integrity in Action */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
              Integrity in Action
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how our commitment to integrity translates into concrete practices that benefit our clients and partners.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {integrityActions.map((action, index) => (
              <Card key={index} className="bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-semibold text-vincotek-dark">
                      {action.title}
                    </h3>
                    <span className="text-sm font-bold text-vincotek-blue bg-vincotek-blue/10 px-3 py-1 rounded-full">
                      {action.metrics}
                    </span>
                  </div>
                  <p className="text-gray-600 leading-relaxed">
                    {action.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Commitment Statement */}
      <section className="py-20 bg-vincotek-dark text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-8">
              Our Integrity Commitment
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="text-center">
                <div className="text-4xl font-bold text-vincotek-yellow mb-2">100%</div>
                <p className="text-gray-300">Transparent Communication</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-vincotek-yellow mb-2">Zero</div>
                <p className="text-gray-300">Hidden Fees or Costs</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-vincotek-yellow mb-2">24/7</div>
                <p className="text-gray-300">Ethical Standards</p>
              </div>
            </div>
            
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              We pledge to maintain the highest standards of integrity in all our business relationships. 
              This isn't just a policy—it's who we are and how we operate every single day.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="bg-vincotek-yellow hover:bg-yellow-400 text-vincotek-dark px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg">
                  Work With Us
                </Button>
              </Link>
              <Link to="/about">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-vincotek-dark px-8 py-3 rounded-full transition-all duration-300">
                  Meet Our Team
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Integrity;